// src/pages/AboutPage.js
/**
 * AboutPage
 * -----------------------------------------------------------------------------
 * Requirements covered:
 * - UKCA mark + approved body number (EN) OR "Exclusively for clinical investigations"
 * - PMDA number (JA) OR "治験専用"
 * - Manufacturer name & address
 * - App name
 * - App version (from package.json via injected env / version.json)
 * - Date of manufacture (release/build date)
 * - "Consult IFU" symbol (+ Japanese text)
 * - Intended purpose (i18n text)
 * - Indicate medical device
 * - Make software version accessible to user
 */

import React, { memo } from "react";
import { useTranslation } from "react-i18next";
import { LABELLING } from "../config/labelling";
import { usePresentationLabelling } from "../hooks/usePresentationLabelling";

// Choose ONE of the following depending on which option you implemented:
import { APP_VERSION, BUILD_DATE } from "../utils/version";
import { Link } from "react-router-dom";
import { IFUIcon } from "../components/icons/IFUIcon";
import { buildLegalDocumentPath } from "../utils/legal/legalStatus";

function Row({ label, children }) {
  return (
    <div className="grid grid-cols-[10ch_1fr] gap-2 justify-start items-start text-start ">
      <div className="">{label}</div>
      <div className="font-semibold">{children}</div>
    </div>
  );
}

function AboutPage() {
  const { t, i18n } = useTranslation(["common", "legal"]);
  const {
    appName,
    intendedUseStatement,
    exclusivelyForClinicalInvestigations,
  } = usePresentationLabelling("dashboard");
  const medicalLogos = ["Asset 2.svg", "Asset 3.svg", "Asset 4.svg"];
  // Normalise to a slug we have pages for
  const lang = (
    i18n.resolvedLanguage ||
    i18n.language ||
    "en-GB"
  ).toLowerCase();

  const hrefIFU = `https://www.electronrx.com/instructions-for-use-pdx1-${lang}`;

  const isEN = i18n.language?.toLowerCase().startsWith("en");
  const isJA = i18n.language?.toLowerCase().startsWith("ja");

  // Regulatory display logic
  const showUKCA = isEN && LABELLING.APPROVED_BODY_NUMBER;

  const showPMDA = isJA && LABELLING.PMDA_NUMBER;
  const showInvestigationOnly = exclusivelyForClinicalInvestigations === true;

  return (
    <div className="flex h-full w-full flex-col gap-4 px-4 pb-4">
      {/* Labelling block */}
      <section
        aria-labelledby="labelling-heading"
        className="flex flex-col gap-4 min-h-full h-fit"
      >
        <h2 id="labelling-heading" className="title-text">
          {t("labelling.heading")}
        </h2>

        <div
          data-testid="about-labelling-card"
          className="card p-4 flex flex-col gap-4 min-h-full"
        >
          {/* Intended purpose */}
          <p className="max-w-lg">
            {intendedUseStatement || t("labelling.intendedPurpose")}
          </p>
          {/* <Row label={t("labelling.intendedPurpose")}>
      
          </Row> */}

          {/* English: UKCA + Approved Body OR Investigation-only text */}
          {isEN &&
            (showUKCA ? (
              <div className="inline-flex items-center gap-2">
                <img
                  src="/assets/ukca.svg"
                  alt="UKCA mark"
                  className="h-5 w-auto"
                  loading="lazy"
                />
                <div>
                  {t("labelling.approvedBody")}:{" "}
                  {LABELLING.APPROVED_BODY_NUMBER}
                </div>
              </div>
            ) : showInvestigationOnly ? (
              <p>{t("labelling.investigationOnly")}</p>
            ) : null)}

          {/* Japanese: PMDA number OR 治験専用 */}
          {isJA &&
            (showPMDA ? (
              <p>{LABELLING.PMDA_NUMBER}</p>
            ) : showInvestigationOnly ? (
              <p>{t("labelling.investigationOnly")}</p>
            ) : null)}
          {/* App name */}
          <Row label={t("labelling.appName")}>{appName}</Row>

          {/* Version (from package.json via env or version.json) */}
          <Row label={t("labelling.version")}>{APP_VERSION}</Row>

          {/* Date of manufacture / build date */}
          <Row
            label={
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 205 255"
                className="fill-transparent h-6 lg:h-8 shrink-0"
                aria-hidden="true"
              >
                <path
                  stroke="#000"
                  strokeWidth="5"
                  d="M2.5 252.5v-120l25-50 25 50 25-50 25 50 25-50 25 50V2.5h50v250z"
                />
              </svg>
            }
          >
            {BUILD_DATE}
          </Row>

          {/* Manufacturer */}
          <Row
            label={
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 205 255"
                className="fill-black h-6 lg:h-8 shrink-0"
                aria-hidden="true"
              >
                <path
                  stroke="#000"
                  strokeWidth="5"
                  d="M2.5 252.5v-120l25-50 25 50 25-50 25 50 25-50 25 50V2.5h50v250z"
                />
              </svg>
            }
          >
            <address className="flex flex-col not-italic leading-tight">
              <span>{t("company.name")}</span>
              <span>{t("company.address1")}</span>
              <span>{t("company.address2")}</span>
              <span>{t("company.address3")}</span>
            </address>
          </Row>
          <Row label={<IFUIcon className="h-10 w-10 fill-greyscale-2" />}>
            <Link
              to={hrefIFU}
              target="_blank"
              rel="noopener noreferrer"
              className=""
            >
              <p className="link-text">{t("home.ifu")}</p>
            </Link>
          </Row>
          <div className="rounded-2xl border border-greyscale-1 p-4 flex flex-col gap-2 max-w-lg">
            <h3 className="subtitle-text">
              {t("aboutSection.title", {
                ns: "legal",
                defaultValue: "Legal information",
              })}
            </h3>
            <p className="text-sm text-greyscale-2">
              {t("aboutSection.body", {
                ns: "legal",
                defaultValue:
                  "Open the current approved Terms & Conditions and Privacy Policy.",
              })}
            </p>
            <div className="flex flex-wrap gap-2">
              <Link
                to={buildLegalDocumentPath("terms")}
                className="small-outlined-button"
              >
                {t("links.terms", {
                  ns: "legal",
                  defaultValue: "Terms & Conditions",
                })}
              </Link>
              <Link
                to={buildLegalDocumentPath("privacy")}
                className="small-outlined-button"
              >
                {t("links.privacy", {
                  ns: "legal",
                  defaultValue: "Privacy Policy",
                })}
              </Link>
            </div>
          </div>
          {/* Top: partner / medical logos */}
          <div className="w-full flex items-start justify-start gap-4">
            {medicalLogos.map((file) => (
              <img
                key={file}
                src={`/assets/${file}`}
                alt={`medical logo ${file.replace(/\.svg$/i, "")}`}
                className="h-8 w-auto"
                loading="lazy"
              />
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}

export default memo(AboutPage);
